import type { Joint } from "@/shared/state/visualization/schema/v1/three-d";

import type { Articulation } from "./articulation";

/**
 * The model's joints, each resolved to the node it moves.
 *
 * The bridge between the extension as the document spells it — links addressed
 * by index, nodes addressed by index — and the panel's schema, which addresses
 * everything by name. Nothing about the articulation is interpreted here beyond
 * that resolution; type, axis, limits and the mimic relation come across as read.
 *
 * A joint whose child link names no node of the file is **dropped**: there would
 * be nothing for a value to move. That is a filter, and it is why the mimic
 * relation must already be resolved to a *name* by the time it arrives —
 * `articulation.ts` does that while the document's own joint array is intact.
 * Resolving an index against this filtered list would silently address the wrong
 * joint, which for a parallel gripper means a finger following the wrong driver.
 * A joint left pointing at a dropped driver is not repaired here either: it is
 * still a truthful record of what the file declared, and `mimicOrder` is what
 * decides such a joint cannot be evaluated.
 *
 * @param articulation the kinematics the file declared, if it declared any
 */
export function readJointsFromArticulation(
  articulation: Articulation | undefined,
): Joint[] {
  if (!articulation) {
    return [];
  }
  return articulation.joints.flatMap((joint) => {
    const node = articulation.links[joint.childLink]?.nodeName;
    if (node === undefined) {
      return [];
    }
    return [
      {
        name: joint.name,
        type: joint.type,
        node,
        ...(joint.axis ? { axis: joint.axis } : {}),
        ...(joint.lower !== undefined ? { lower: joint.lower } : {}),
        ...(joint.upper !== undefined ? { upper: joint.upper } : {}),
        ...(joint.mimic !== undefined ? { mimic: joint.mimic } : {}),
        // Copied onto the joint exactly as `axis` and the limits are, so the
        // settings can say what the file declared without re-reading it — and
        // so "reset this joint to the model's default" has something to reset
        // to. It is a declaration, not a binding: resolving it against the
        // topics a workspace has open is `resolveDefaultBindings`' job.
        ...(joint.binding !== undefined
          ? { defaultBinding: joint.binding }
          : {}),
      },
    ];
  });
}
